import type { Session, SessionContentStatus } from '../../../../types';
import { isCheckinOneOnOneType } from '../../../../types';
import { formatDayOfWeek } from '../../../../utils/formatters';
import { getDisplayTimezone, getTimezoneAbbreviation } from '../../../../utils/timezone';
import type { TimeSlotResponse } from '../../../../services/api/timeSlots';
import type { GroupWithMembers } from '../../../../services/api/groups';
import type { SessionWithDetailsAndRsvp } from '../../../../services/api/sessions';

/**
 * Extended group type that includes organized session data and scheduling info.
 */
export interface GroupWithSessions extends GroupWithMembers {
  sessions: SessionWithDetailsAndRsvp[];
  scheduledTimeSlot?: TimeSlotResponse;
  isFinalized?: boolean;
  // MED-838: Unique participants who have not responded to any invite for this
  // group. Sourced from the finalization-status endpoint so the resend-invites
  // callout can show a non-inflated count.
  pendingUniqueCount?: number;
}

/**
 * Display status for a session, computed from its scheduled end time and attendance state.
 */
export interface DisplayStatus {
  label: string;
  color: string;
}

/**
 * Compute the display status for a session based on its state.
 * Priority: missed > cancelled > completed/attendance_marked > past end time > scheduled.
 */
export function getDisplayStatus(session: Session): DisplayStatus {
  // MED-1007 (E4.0g · AC1/AC4): a `missed` session never took place. It must
  // short-circuit before the attendance/past-end-time checks so it never reads
  // as "Awaiting Attendance" or "Completed" — it renders as a distinct "Missed".
  if (session.status === 'missed') {
    return { label: 'Missed', color: 'orange' };
  }

  if (session.status === 'cancelled') {
    return { label: 'Cancelled', color: 'red' };
  }

  if (session.attendance_marked || session.status === 'completed') {
    return { label: 'Completed', color: 'green' };
  }

  if (session.scheduled_end && new Date(session.scheduled_end) < new Date()) {
    return { label: 'Awaiting Attendance', color: 'yellow' };
  }

  return { label: 'Scheduled', color: 'blue' };
}

/**
 * MED-1075 / E1.3k — composed Sessions-tab content badge. `primaryKey` indexes
 * SESSION_CONTENT_STATUS_CONFIG for the primary badge (in_progress / complete);
 * `showReview` drives the additive over-length "Review" chip (keyed 'review').
 */
export interface SessionContentBadge {
  primaryKey: 'in_progress' | 'complete';
  primaryTooltip: string;
  showReview: boolean;
  reviewTooltip: string;
}

/**
 * Compose the Sessions-tab content badge from the server-derived content_status.
 * Returns null when NO badge should render: status absent (non-group-coaching —
 * AC9) or the not_started state (AC5). The In-progress tooltip names the
 * outstanding half(s) using the static phrasing from the spec (AC7); the Review
 * tooltip reports the agenda total vs. the configured session length (AC6).
 */
export function getSessionContentBadge(
  contentStatus: SessionContentStatus | null | undefined
): SessionContentBadge | null {
  if (!contentStatus || contentStatus.state === 'not_started') return null;

  const { state, over_length, agenda_total_minutes, session_duration_minutes } = contentStatus;

  let primaryTooltip: string;
  if (state === 'complete') {
    primaryTooltip = 'Content complete — Ready for session leaders and participants';
  } else {
    // MED-1182 (E1.3f-R1) — participant pre-session work is optional and no
    // longer part of completeness, so an in-progress session can only be
    // waiting on its agenda (AC7 outstanding text is now agenda-only).
    primaryTooltip = "In progress — session leaders can't prep until the agenda is complete.";
  }

  const reviewTooltip =
    session_duration_minutes != null
      ? `The agenda totals ${agenda_total_minutes} min, longer than the ${session_duration_minutes} min session length. Consider trimming section durations.`
      : '';

  return {
    primaryKey: state === 'complete' ? 'complete' : 'in_progress',
    primaryTooltip,
    showReview: over_length,
    reviewTooltip,
  };
}

/**
 * Format a schedule badge string from a time slot (e.g., "Tuesday 10:00 AM EST").
 * Returns null if no time slot is available.
 */
export function formatScheduleBadgeText(
  scheduledTimeSlot: TimeSlotResponse | undefined
): string | null {
  if (!scheduledTimeSlot) return null;

  const ts = scheduledTimeSlot;
  const dayStr = formatDayOfWeek(ts.day_of_week);
  const timeStr = ts.display_time || ts.time;
  const tzAbbr = ts.timezone_abbreviation || getTimezoneAbbreviation(ts.timezone);

  return `${dayStr} ${timeStr} ${tzAbbr}`;
}

/**
 * MED-1360: a scheduled 1:1 or check-in whose time has passed with no
 * attendance. It reads as needing attendance, not as scheduled. The end time
 * decides; a row with no end uses its start.
 */
export function isPastAwaitingAttendance(
  session: {
    status: string;
    scheduled_start: string | null;
    scheduled_end?: string | null;
    attendance_marked?: boolean;
  },
  now: number = Date.now(),
): boolean {
  if (session.status !== 'scheduled' || session.attendance_marked) return false;
  const endOrStart = session.scheduled_end ?? session.scheduled_start;
  if (!endOrStart) return false;
  const time = new Date(endOrStart).getTime();
  return !Number.isNaN(time) && time <= now;
}

/**
 * MED-1350: the number of check-ins that are on (0, 1, or 2). Each one is one
 * more 1:1 row per participant.
 */
export function enabledCheckinCount(cohort: {
  intro_checkin_enabled?: boolean;
  midpoint_checkin_enabled?: boolean;
}): number {
  return (cohort.intro_checkin_enabled ? 1 : 0) + (cohort.midpoint_checkin_enabled ? 1 : 0);
}

/**
 * MED-1350: the CM 1:1 section shows for a 1:1 modality behind the
 * multi_modality flag, and for check-ins whatever the flag says (Review
 * Focus 1). hasCheckins is true when a check-in toggle is on or when the
 * cohort still has live check-in rows, so a booked check-in kept after both
 * toggles go off stays on screen.
 */
export function showsOneOnOneSection(
  modalities: { hasOneOnOne: boolean; hasCheckins: boolean },
  multiModalityEnabled: boolean,
): boolean {
  return (modalities.hasOneOnOne && multiModalityEnabled) || modalities.hasCheckins;
}

// MED-1360: card row date. "May 22" in the current year, "May 22, 2025" in
// another year, so the date fits a 300 px card. Same timezone as formatDate.
export function formatCardDate(date: string, now: Date = new Date()): string {
  const d = new Date(date);
  if (isNaN(d.getTime())) return '';
  const timeZone = getDisplayTimezone();
  const yearOf = (x: Date) => x.toLocaleDateString('en-US', { year: 'numeric', timeZone });
  return d.toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    ...(yearOf(d) !== yearOf(now) ? { year: 'numeric' } : {}),
    timeZone,
  });
}

/** The fields of a 1:1 list row that its completion counter reads. */
export interface CompletionParticipant {
  completed_count: number;
  total_session_count: number;
  sessions: { one_on_one_type?: string | null; status: string; attendance_status?: string | null }[];
}

// MED-1266: the statuses that count as attended (legacy 'present' included).
const ATTENDED_STATUSES = new Set(['attended', 'partial', 'present', 'late']);

// MED-1350: the API counts paid 1:1 rows only. A participant with check-ins
// and no paid rows shows the check-ins it attended instead of "0/0 COMPLETE".
// MED-1360: a row with paid rows also counts its check-ins, so "x/y COMPLETE"
// counts the same sessions as the subtitle ("6 sessions each, including 2
// check-ins"). A check-in is done when it is completed, as a paid row is.
export function getCompletion(participant: CompletionParticipant): {
  done: number;
  total: number;
  label: string;
} {
  const checkins = participant.sessions.filter((s) => isCheckinOneOnOneType(s.one_on_one_type));
  if (participant.total_session_count === 0 && checkins.length > 0) {
    const attended = checkins.filter(
      (s) => !!s.attendance_status && ATTENDED_STATUSES.has(s.attendance_status),
    ).length;
    return { done: attended, total: checkins.length, label: 'check-ins attended' };
  }
  return {
    done: participant.completed_count + checkins.filter((s) => s.status === 'completed').length,
    total: participant.total_session_count + checkins.length,
    label: 'complete',
  };
}
